"use client"

import { HttpClient } from "@/app/api/HttpClient"
import { API_POST_VIEW_PATH } from "@/constants/prefix-path-api"

/**
 * Increment the view count for a post
 * @param postId - The ID of the post
 * @returns Promise<void>
 */
export const incrementPostViews = async (postId: number): Promise<void> => {
  try {
    await HttpClient.patch(`${API_POST_VIEW_PATH}/${postId}/view`)
  } catch (error) {
    console.error("Error incrementing post views:", error)
    // Don't throw error to avoid blocking navigation
  }
}

/**
 * Handle post click - increment views and navigate to post
 * @param postId - The ID of the post
 * @param slug - The slug of the post
 * @param router - Next.js router instance (optional, if not provided will use window.location)
 * @returns Promise<void>
 */
export const handlePostClick = async (
  postId: number,
  slug: string,
  router?: { push: (url: string) => void },
): Promise<void> => {
  try {
    // Increment views in the background (don't wait for it)
    incrementPostViews(postId).catch(error => {
      console.error("Failed to increment views:", error)
    })

    // Navigate to the post immediately
    if (router) {
      router.push(`/${slug}`)
    } else {
      window.location.href = `/${slug}`
    }
  } catch (error) {
    console.error("Error handling post click:", error)
    // Still try to navigate even if view increment fails
    if (router) {
      router.push(`/${slug}`)
    } else {
      window.location.href = `/${slug}`
    }
  }
}

/**
 * Create a post click handler function for React components
 * @param postId - The ID of the post
 * @param slug - The slug of the post
 * @param router - Next.js router instance (optional)
 * @returns Click handler function
 */
export const createPostClickHandler = (
  postId: number,
  slug: string,
  router?: { push: (url: string) => void },
) => {
  return (event?: React.MouseEvent) => {
    // Prevent default link behavior if event is provided
    if (event) {
      event.preventDefault()
    }

    handlePostClick(postId, slug, router)
  }
}

/**
 * Handle post click for Next.js Link component
 * Use this with onClick handler on Link component
 * @param postId - The ID of the post
 * @returns Click handler function
 */
export const handlePostClickForLink = (postId: number) => {
  return (event: React.MouseEvent) => {
    // Don't prevent default - let Link handle navigation
    // Just increment views in the background
    incrementPostViews(postId).catch(error => {
      console.error("Failed to increment views:", error)
    })
  }
}
